import { useEffect, useState } from 'react';
import { api, ApiError } from '@/lib/api';
import type { CommissionProgress } from '@/lib/types';

/**
 * One person's invoiced revenue against their commission assignment's targets
 * for one period (`GET /commissions/progress`).
 *
 * `data: null` covers: either id missing, no assignment for that period, and a
 * 403 (the reader may not see this person) — all of which hide the strip.
 * `loading` is derived from a stamped key, so a change of person or period
 * reads as loading rather than showing the previous answer for a frame.
 */
export interface CommissionProgressState {
  data: CommissionProgress | null;
  loading: boolean;
  error: ApiError | Error | null;
}

export function useCommissionProgress(
  userId: number | null,
  periodId: number | null,
): CommissionProgressState {
  const [answer, setAnswer] = useState<{
    key: string;
    data: CommissionProgress | null;
    error: ApiError | Error | null;
  } | null>(null);

  const key = userId === null || periodId === null ? '' : `${userId}|${periodId}`;

  useEffect(() => {
    if (userId === null || periodId === null) return;
    const controller = new AbortController();
    const { signal } = controller;
    const stamp = `${userId}|${periodId}`;

    api
      .commissionProgress({ userId, periodId }, signal)
      .then((envelope) => {
        if (signal.aborted) return;
        setAnswer({ key: stamp, data: envelope.data, error: null });
      })
      .catch((caught: unknown) => {
        if (signal.aborted) return;
        if (caught instanceof DOMException && caught.name === 'AbortError') return;
        if (caught instanceof ApiError && (caught.status === 403 || caught.code === 'FORBIDDEN')) {
          setAnswer({ key: stamp, data: null, error: null });
          return;
        }
        setAnswer({
          key: stamp,
          data: null,
          error: caught instanceof Error ? caught : new Error('Could not read commission progress.'),
        });
      });

    return () => controller.abort();
  }, [userId, periodId]);

  if (key === '') return { data: null, loading: false, error: null };
  const current = answer !== null && answer.key === key ? answer : null;
  if (current === null) return { data: null, loading: true, error: null };
  return { data: current.data, loading: false, error: current.error };
}
